import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * <SEOHead /> — drop-in title / meta / OG / canonical tag manager.
 *
 * Every public-facing page should render one of these at the top of its
 * tree. Defaults fall back to the site-wide brand copy so partial pages
 * (e.g. just title + description) still produce a valid <head>.
 */
const SITE_NAME = "HealthFairX";
const DEFAULT_DESCRIPTION =
    "HealthFairX connects corporate hosts with healthcare and wellness vendors for employee health fairs. Browse vetted vendors by city, category, and rating.";
const DEFAULT_OG_IMAGE = "/og-card.png";

export function SEOHead({
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_OG_IMAGE,
    type = "website",
    canonical,
    noindex = false,
    children,
}) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Vendors for corporate health fairs`;
    // Build absolute canonical from current origin when not explicitly provided.
    const url = canonical || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    const absoluteImage = image && image.startsWith("http")
        ? image
        : (typeof window !== "undefined" ? `${window.location.origin}${image}` : image);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            {url && <link rel="canonical" href={url} />}

            {/* Open Graph */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            {url && <meta property="og:url" content={url} />}
            {absoluteImage && <meta property="og:image" content={absoluteImage} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            {absoluteImage && <meta name="twitter:image" content={absoluteImage} />}

            {children}
        </Helmet>
    );
}

/**
 * <JsonLd /> — renders a `<script type="application/ld+json">` block.
 *
 * Pass a structured-data object directly; the component handles stringify
 * and React's "dangerously" escape requirements. Multiple blocks per page
 * are fine — search engines parse each independently.
 */
export function JsonLd({ data }) {
    if (!data) return null;
    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(data)}</script>
        </Helmet>
    );
}

/**
 * usePageView — fires a one-shot POST /api/seo/track on route change.
 *
 * Lightweight, fire-and-forget. Failures are swallowed (analytics should
 * never break user flow). Bucketing happens server-side by day so volume
 * stays bounded.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export function usePageView() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (!BACKEND) return;
        const url = `${BACKEND}/api/seo/track`;
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ route: pathname }),
            keepalive: true,
        }).catch(() => { /* analytics never blocks UX */ });
    }, [pathname]);
}

/**
 * Trust-aware aggregate rating builder for JSON-LD. Returns `null` when
 * the vendor has fewer than 3 approved reviews (we don't want to publish
 * misleading aggregate stars below the threshold).
 */
export function buildAggregateRating(trust) {
    const r = trust?.rating;
    if (!r?.show_public_rating || !r?.avg_stars) return null;
    return {
        "@type": "AggregateRating",
        ratingValue: r.avg_stars,
        reviewCount: r.approved_count,
        bestRating: 5,
        worstRating: 1,
    };
}
